import { useState, ReactNode } from 'react';
import AssetInput from '../../../../components/AssetInput';
import PercentageSelector from '../../../../components/PercentageSelector';
import { config } from '../../../../config/config';
import {
    EnabledChains,
    OrderBookType,
    Tokens,
    Validators,
} from '../../../../config/constants';
import useModalState from '../../../../context/modal/useModalState';
import { formatUSD } from '../../../../helpers';
import { UserPositionRaw } from '../../../../hooks/web3/trade/useUserPositions';
import useTokenDetails from '../../../../hooks/web3/useTokenDetails';
import { preventOverflow } from '../../../../web3/utils';
import { parseEther } from 'ethers/lib/utils';
import { INSUFFICIENT_BALANCE } from '../../../../config/constants';
import { useWeb3React } from '@web3-react/core';
import { BigNumber } from 'ethers';
import CloseMarketPreview from './CloseMarketPreview';
import useCreateOrder, {
    CreateOrderOptions,
} from '../../../../hooks/web3/trade/useCreateOrder';

export type ClosePositionMarketProps = {
    children(confirm: {
        confirm: () => Promise<void>;
        validators: Validators[];
    }): ReactNode;
};

const ClosePositionMarket: React.FC<ClosePositionMarketProps> = ({ children }) => {
    const { modalData } = useModalState();
    const { chainId } = useWeb3React();
    const createOrder = useCreateOrder();

    const userPosition: UserPositionRaw | undefined = modalData.data.position;

    const [amount, setAmount] = useState('');

    const { decimals } = useTokenDetails(
        config.addresses[chainId as EnabledChains[number]]?.collateral
    );

    const validators: Validators[] = [
        {
            errorCondition: parseEther(amount || '0').isZero(),
            message: 'Close Size is required!',
        },
        {
            errorCondition: parseEther(amount || '0').gt(
                parseEther(userPosition?.volume || '0')
            ),
            message: INSUFFICIENT_BALANCE,
        },
    ];

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = e.target;

        setAmount(preventOverflow(value, decimals));
    };

    const handleMax = () =>
        setAmount(
            formatUSD(userPosition?.volume || '0', {
                useGrouping: false,
                maxDecimals: 18,
                currency: '',
            })
        );

    const confirm = async () => {
        if (!userPosition) throw new Error('UserPosition is undefined');

        const PERCENTAGE_MULTIPLIER = 1_000_000;

        const removedPercentage = parseEther(amount || '0')
            .mul(parseEther('1'))
            .div(parseEther(userPosition?.volume || '1'));

        const isFullAmount = removedPercentage.eq(parseEther('1'));

        const options: CreateOrderOptions = {
            isMarketOrder: true,
            tradePair: userPosition.tradePair,
            userPosition,
        };

        if (isFullAmount) {
            await createOrder(
                OrderBookType.CLOSE,
                {
                    positionId: userPosition.id,
                    tradePair: userPosition.tradePair.address,
                },
                'ClosePositionOrder',
                userPosition.isShort,
                () => '0',
                options
            );
        } else {
            await createOrder(
                OrderBookType.PARTIALLY_CLOSE,
                {
                    positionId: userPosition.id,
                    proportion: BigNumber.from(removedPercentage)
                        .mul(PERCENTAGE_MULTIPLIER)
                        .div(parseEther('1'))
                        .toString(),
                    tradePair: userPosition.tradePair.address,
                },
                'PartiallyClosePositionOrder',
                userPosition.isShort,
                () => userPosition.bankruptcyPrice,
                options
            );
        }
    };

    return (
        <>
            <AssetInput
                label='Size'
                sideLabel={
                    <>
                        Max:{' '}
                        {formatUSD(userPosition?.volume || 0, {
                            maxDecimals: 4,
                            currency: '',
                        })}
                    </>
                }
                name='returnSize'
                value={amount}
                onChange={handleChange}
                asset={Tokens.USDC}
                onMax={handleMax}
            />
            <PercentageSelector
                currentValue={amount}
                totalValue={userPosition?.volume || '0'}
                handleChange={(value) => setAmount(preventOverflow(value, decimals))}
                decimals={decimals}
            />
            <CloseMarketPreview amount={amount} userPosition={userPosition} />
            {children({ confirm, validators })}
        </>
    );
};

export default ClosePositionMarket;
